import type { Municipality, PrefPoint } from '../data/types';

/** 住所照合の候補（元仕様 §9）。 */
export interface PlaceHit {
  label: string;
  lon: number;
  lat: number;
  level: '市区町村' | '都道府県';
}

interface Place extends Municipality {
  /** 政令市の区から作った「市」全体の代表地点か。 */
  agg: boolean;
}

const short = (pref: string) => pref.replace(/(都|府|県)$/, '');

/**
 * 住所の文字から市区町村の代表地点を、通信せずに探す（FR-SRCH-06）。
 * サンプルの `lookupAddress` と同じ手順。
 * 1. NFKC にして空白を除く。2 文字未満は探さない。
 * 2. 先頭が都道府県名（または「都・府・県」を除いた名前）なら、その県の中だけを探す。
 * 3. 残りの文字が市区町村名で始まるか、残りの文字（2 文字以上）で市区町村名が始まるものを候補にする。
 *    政令市は区の平均を「市」の代表地点として足してある。
 * 4. 長く一致したものを先に、同じなら市全体を先にして、最大 3 件。
 *    市区町村が見つからず都道府県だけ分かったときは、県の代表地点を返す。
 */
export class AddressIndex {
  private readonly places: Place[];

  constructor(
    private readonly prefs: readonly PrefPoint[],
    cities: readonly Municipality[],
  ) {
    const agg = new Map<string, { pref: string; name: string; pts: Municipality[] }>();
    for (const c of cities) {
      const m = /^(.+?市)(.+区)$/.exec(c.name);
      if (!m) continue;
      const key = `${c.pref}|${m[1]}`;
      const a = agg.get(key) ?? { pref: c.pref, name: m[1]!, pts: [] };
      a.pts.push(c);
      agg.set(key, a);
    }
    this.places = [
      ...cities.map((c) => ({ ...c, agg: false })),
      ...[...agg.values()].map((a) => ({
        pref: a.pref,
        name: a.name,
        lon: a.pts.reduce((s, c) => s + c.lon, 0) / a.pts.length,
        lat: a.pts.reduce((s, c) => s + c.lat, 0) / a.pts.length,
        agg: true,
      })),
    ];
  }

  lookup(q: string | null | undefined): PlaceHit[] {
    const t = (q ?? '').normalize('NFKC').replace(/\s/g, '');
    if (t.length < 2) return [];
    const pref =
      this.prefs.find((p) => t.startsWith(p.name)) ??
      this.prefs.find((p) => t.startsWith(short(p.name)));
    const rest = pref
      ? t.slice(t.startsWith(pref.name) ? pref.name.length : short(pref.name).length)
      : t;
    const pool = pref ? this.places.filter((c) => c.pref === pref.name) : this.places;
    const hits = pool.filter(
      (c) => rest.startsWith(c.name) || (rest.length >= 2 && c.name.startsWith(rest)),
    );
    const matched = (c: Place) => (rest.startsWith(c.name) ? c.name.length : 0);
    hits.sort((a, b) => matched(b) - matched(a) || Number(b.agg) - Number(a.agg));
    const out: PlaceHit[] = hits
      .slice(0, 3)
      .map((c) => ({ label: c.pref + c.name, lon: c.lon, lat: c.lat, level: '市区町村' }));
    if (!out.length && pref)
      out.push({ label: pref.name, lon: pref.lon, lat: pref.lat, level: '都道府県' });
    return out;
  }
}
